/**
 * Live arrivals, without moving anybody's place.
 *
 * The client holds a sequence number rather than a timestamp (ADR-0009), so
 * "what is new" and "I have been away for an hour" are the same question. Arrivals
 * are counted, not injected: an alert that appears above the cursor mid-triage moves
 * every row under the analyst's hands, and the analyst then closes the wrong one.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { request } from './client'
import type { FindingSummary } from './types'

interface Changes {
  since_sequence: number
  sequence: number
  latest_sequence: number
  more: boolean
  results: FindingSummary[]
}

export interface Live {
  /** How many alerts have arrived since the queue was last shown. */
  pending: number
  /** The highest severity among them, so "3 new" can say whether to care. */
  worstPending: number
  /** Whether polling itself is failing — silence must not look like quiet. */
  error: unknown
  /** Take the pending arrivals: clears the count and refreshes the list. */
  accept: () => void
}

export function useLiveFindings(
  enabled: boolean,
  onAccept: () => void,
  tenant?: string,
): Live {
  const [pending, setPending] = useState<FindingSummary[]>([])
  const position = useRef<number | null>(null)

  const query = useQuery({
    queryKey: ['finding-changes', tenant],
    queryFn: async () => {
      const since = position.current ?? 0
      const scope = tenant ? `&tenant=${encodeURIComponent(tenant)}` : ''
      const changes = await request<Changes>(
        `/findings/changes/?since_sequence=${since}${scope}`,
      )
      // The first poll of a session establishes the position without announcing the
      // entire history as "new".
      if (position.current === null) {
        position.current = changes.latest_sequence
        return changes
      }
      position.current = changes.sequence
      if (changes.results.length > 0) {
        setPending((current) => [...current, ...changes.results])
      }
      return changes
    },
    enabled,
    refetchInterval: 5_000,
    retry: false,
  })

  // A poll that keeps failing is reported rather than absorbed: a queue that has
  // silently stopped updating looks exactly like a quiet night.
  useEffect(() => {
    if (!enabled) setPending([])
  }, [enabled])

  return {
    pending: pending.length,
    worstPending: pending.reduce((worst, item) => Math.max(worst, item.severity_id), 0),
    error: query.isError ? query.error : null,
    accept: () => {
      setPending([])
      onAccept()
    },
  }
}
